import { LocalStorageKeys } from '@/constants/LocalStorageKeys';
import { IDataPointsAddFields, newAdditionalFields } from '@/models/AdditionalFields';
import IFieldData from '@/models/forms/IFieldData';
import IFormCodeFields from '@/models/forms/IFormCodeFields';
import IFormFields from '@/models/forms/IFormFields';
import FormsService from '@/services/3.6/FormsService';
import TheWebEnvConfigService from '@/services/TheWebEnvConfigService';
import { formFieldsStoreV36, Getters, Mutations, store } from '@/store';
import { Action, Module, Mutation, VuexModule } from 'vuex-module-decorators';
import theLocalStorageUtils from '@/utils/LocalStorageUtils';
import IFormFieldsAdditionalDetails from '@/models/forms/IFormFieldsAdditionalDetails';
import IFormCodeFieldsAdditionalDetails from '@/models/forms/IFormCodeFieldsAdditionalDetails';
import IFieldSubjectCompRelationship from '@/models/forms/IFieldSubjectCompRelationship';

export default {
    /**
     * Fetch form fields from the service using the given formCode and put into the store.
     * Replaces any existing form fields in the store for the given formCode.
     * When certain changes to fields are made the form fields must be reloaded.
     * @param formCode
     * @returns the form fields array, empty on an error or unsupported form code
     */
    async fetchAndStoreFormFields(formCode: string): Promise<IFormFields> {
        await fetchAndStore(formCode);
        const formFields = formFieldsStoreV36.FormFields;
        return formFields;
    },

    async fetchAndStoreFormFieldsAdditionalDetails(formCode: string): Promise<IFormFieldsAdditionalDetails> {
        await fetchAndStoreFieldsAdditionalDetails(formCode);
        const formFieldsAdditionalDetails = formFieldsStoreV36.FormFieldsAdditionalDetails;
        return formFieldsAdditionalDetails;
    },

    async fetchAndStoreSubjectCompRelationship(isSubject: boolean): Promise<IFieldSubjectCompRelationship> {
        await fetchSubjectCompRelationship(isSubject);
        const subjectCompRelationship = formFieldsStoreV36.SubjectCompRelationship;
        return subjectCompRelationship;
    },

    /**
     * Get form fields from the store for the form code found in local storage.
     * When none are found in the store, will fetch from the service and place into the store.
     * @returns the form fields array, empty on an error or unsupported form code
     */
    async getFormFields(): Promise<IFormFields> {
        const formCode = theLocalStorageUtils.get(LocalStorageKeys.formCode) ?? '';
        let formFields = formFieldsStoreV36.FormFields;
        if (formFields?.length > 0) {
            return formFields;
        }

        formFields = await this.fetchAndStoreFormFields(formCode);
        return formFields;
    },

    async getFieldDataById(fieldId: number): Promise<IFieldData | null> {
        const formFields = await this.getFormFields();
        let fieldData = formFields.find((fd) => fd.fieldId === fieldId);
        if (!fieldData && typeof fieldId === 'number' && fieldId % 1 !== 0) {
            fieldData = formFields.find((fd) => fd.fieldId === Math.floor(fieldId));
        }
        return fieldData ?? null;
    },

    async getFieldDataByName(fieldName: string): Promise<IFieldData | null> {
        const formFields = await this.getFormFields();
        const fieldData = formFields.find((fd) => fd.fieldName === fieldName);
        return fieldData ?? null;
    },

    // async getAdditionalFields(): Promise<IDataPointsAddFields> {
    //     let additionalFields = formFieldsStoreV36.AdditionalFields;
    //     if (additionalFields) {
    //         return additionalFields;
    //     }

    //     await fetchAdditionalFields();
    //     additionalFields = formFieldsStoreV36.AdditionalFields;
    //     return additionalFields ?? newAdditionalFields();
    // },

    // async fetchAndStoreAdditionalFields(): Promise<IDataPointsAddFields> {
    //     await fetchAdditionalFields();
    //     const additionalFields = formFieldsStoreV36.AdditionalFields;
    //     return additionalFields ?? newAdditionalFields();
    // },
};

async function fetchAndStore(formCode: string) {
    const webEnvConfig = await TheWebEnvConfigService.getInstance().getConfigByHost();
    let formFields = await new FormsService(webEnvConfig).getDataFields(formCode);

    // Need to change the SUB_PHOTO to be COMP_PHOTO so that the photo field will be properly recognized 
    // and handled by the system. 
    if (formFields) {
        formFields.forEach((field) => { 
            if (field.fieldName === 'SUB_PHOTO') {
                field.fieldName = 'COMP_PHOTO';
            }           
        });
    }

    if (!formFields) {
        formFields = [];
    }

    const formCodeFields: IFormCodeFields = {
        formCode: formCode,
        formFields: formFields,
    };

    formFieldsStoreV36.updateFields(formCodeFields);
}

async function fetchAndStoreFieldsAdditionalDetails(formCode: string) {
    const webEnvConfig = await TheWebEnvConfigService.getInstance().getConfigByHost();
    let formFields = await new FormsService(webEnvConfig).getDataFieldsAdditionalDetails(formCode);

    if (!formFields) {
        formFields = {} as IFormFieldsAdditionalDetails;
    }

    const formCodeFields: IFormCodeFieldsAdditionalDetails = {
        formCode: formCode,
        formFields: formFields,
    };

    formFieldsStoreV36.updateFieldsAdditionalDetails(formCodeFields);
}

async function fetchSubjectCompRelationship(isSubject: boolean): Promise<void> {
    const formCode = theLocalStorageUtils.get(LocalStorageKeys.formCode) || '';
    const webEnvConfig = await TheWebEnvConfigService.getInstance().getConfigByHost();
    let formFields = await new FormsService(webEnvConfig).getSubjectCompRelationship(formCode, isSubject);

    if (!formFields) {
        formFields = {} as IFieldSubjectCompRelationship;
    }

    formFieldsStoreV36.updateSubjectCompRelationship(formFields);
}

// async function fetchAdditionalFields(): Promise<void> {
//     const formCode = theLocalStorageUtils.get(LocalStorageKeys.formCode) || '';
//     const webEnvConfig = await TheWebEnvConfigService.getInstance().getConfigByHost();
//     let result = await new FormsService(webEnvConfig).getAdditionalFields(formCode);

//     if (result.additionalFields.length === 0 && result.dataPoints.length === 0) {
//         result = newAdditionalFields();
//     }

//     formFieldsStoreV36.updateAdditionalFields(result);
// }
